import { Fragment, useCallback, useState, useEffect, useRef, RefObject } from 'react';
import StyleCheckbox from '@components/form/StyleCheckbox';
import { setCategoryList, setSubCategoryList, resetData } from '@features/ui/UiSlice';
import { useAppDispatch } from '@hooks/defineRtkTypedHook';
import type { ICategoryItem, IL10n } from '@type/index';

interface IFilterProps {
  data: ICategoryItem[];
  l10n: IL10n;
}

interface IFilterLayerProps {
  data: ICategoryItem[];
  l10n: IL10n;
  isLayerOpen: boolean;
  setIsLayerOpen: (isLayerOpen: boolean) => void;
  btnFilterRef: RefObject<HTMLButtonElement>;
}

// 서브카테고리가 없는 카테고리 - "전체" 상수 값
const ALL_VALUE = 'ALL';

// checkValue에서 categoryId, subCategoryId 추출
const getCategoryFromCheckValue = (changeList: string[]) => {
  let categoryIdSet = new Set(); // 중복 제거
  let subCategoryIdSet = new Set(); // 중복 제거

  changeList?.forEach((item) => {
    if (item?.includes('-')) {
      const splitValue = item?.split('-');
      splitValue[0] && categoryIdSet?.add(splitValue[0]);
      splitValue[1] && splitValue[1] !== ALL_VALUE && subCategoryIdSet?.add(splitValue[1]);
    }
  });

  return {
    categoryIdList: Array.from(categoryIdSet),
    subCategoryIdList: Array.from(subCategoryIdSet),
  };
};

const FilterLayer = (props: IFilterLayerProps) => {
  const dispatch = useAppDispatch();
  const { data, l10n, isLayerOpen, setIsLayerOpen, btnFilterRef } = props || {};
  const [unfoldCategoryId, setUnfoldCategoryId] = useState<string[]>([]);
  const [checkValue, setCheckValue] = useState<string[]>([]);
  const layerRef = useRef<HTMLDivElement>(null);

  const checkboxChangeCallback = useCallback(
    (value: string) => {
      const hasValue = checkValue?.includes(value);
      const changeList = hasValue ? checkValue?.filter((v) => v !== value) : [...checkValue, value];
      const { categoryIdList, subCategoryIdList } = getCategoryFromCheckValue(changeList);

      dispatch(setCategoryList(categoryIdList));
      dispatch(setSubCategoryList(subCategoryIdList));
      dispatch(resetData()); // 가공 데이터 초기화

      setCheckValue(changeList);
    },
    [checkValue],
  );

  const documentClickCallback = useCallback(
    (e: MouseEvent) => {
      const target = e?.target as Element;
      const layerEle = layerRef?.current;
      const btnFilterEle = btnFilterRef?.current;

      if (!layerEle?.contains(target) && target !== btnFilterEle) {
        setIsLayerOpen(false);
      }
    },
    [layerRef],
  );

  const categoryClickCallback = useCallback(
    (id: string) => {
      // const isMe = unfoldCategoryId?.length > 0 && unfoldCategoryId?.includes(id); // 하나만 펼쳐지도록
      // setUnfoldCategoryId(isMe ? [] : [id]);

      const isMe = unfoldCategoryId?.length > 0 && unfoldCategoryId?.includes(id);
      setUnfoldCategoryId(isMe ? unfoldCategoryId?.filter((v: string) => v !== id) : [...unfoldCategoryId, id]);
    },
    [unfoldCategoryId],
  );

  const resetClickCallback = useCallback(() => {
    setCheckValue([]);
    dispatch(setCategoryList([]));
    dispatch(setSubCategoryList([]));
    dispatch(resetData()); // 가공 데이터 초기화
  }, []);

  useEffect(() => {
    if (isLayerOpen) {
      document.addEventListener('click', documentClickCallback);
    }

    // unmount
    return () => {
      document?.removeEventListener('click', documentClickCallback);
      setUnfoldCategoryId([]);
    };
  }, [isLayerOpen]);

  return (
    <div className={`schedule-filter-layer ${isLayerOpen ? 'is-open' : ''}`} ref={layerRef}>
      <div className="schedule-filter-header">
        <h1 className="schedule-filter-title">Filter</h1>
        <div className="schedule-filter-reset">
          <button type="button" className="btn-reset" onClick={resetClickCallback}>
            <span className="hidden">{l10n?.filter?.btnResetText}</span>
          </button>
        </div>
      </div>
      <div className="schedule-filter-content">
        <ul className="schedule-filter-list">
          {Array.isArray(data) && data?.length > 0 ? (
            data?.map((item) => {
              const { name: categoryName, id: categoryId, optionalCategoryList } = item || {};
              const hasSubCategory = Array.isArray(optionalCategoryList) && optionalCategoryList?.length > 0;

              const subCategoryListArr = hasSubCategory
                ? optionalCategoryList
                : [{ id: ALL_VALUE, name: categoryName }];

              return (
                <li
                  key={categoryId}
                  className={`schedule-filter-item ${unfoldCategoryId?.includes(categoryId) ? 'is-active' : ''}`}
                >
                  <div
                    className="schedule-filter-category"
                    onClick={() => categoryClickCallback(categoryId)}
                    data-contents="필터_카테고리_클릭"
                  >
                    <h2 className="category-title">{categoryName}</h2>
                    <div className="category-fold">
                      <button type="button" className="btn-fold">
                        <span className="hidden">
                          {categoryName} {l10n?.filter?.btnFoldText}
                        </span>
                      </button>
                    </div>
                  </div>
                  <div className="schedule-filter-subcategory">
                    <ul>
                      {subCategoryListArr?.map((subCategoryItem) => {
                        const { id: subCategoryId, name: subCategoryName } = subCategoryItem || {};
                        const value = `${categoryId}-${subCategoryId}`; // 서브 카테고리는 다른 카테고리에서도 사용할 수 있으므로, 카테고리-서브카테고리 로 value 생성

                        return (
                          <li key={value} className="schedule-filter-subcategory-item">
                            <StyleCheckbox
                              isChecked={checkValue?.includes(value)}
                              value={value}
                              label={subCategoryName}
                              changeCallback={checkboxChangeCallback}
                              ga="필터_카테고리_클릭"
                            />
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </li>
              );
            })
          ) : (
            <Fragment />
          )}
        </ul>
      </div>
    </div>
  );
};

const Filter = ({ data, l10n }: IFilterProps) => {
  const [isLayerOpen, setIsLayerOpen] = useState<boolean>(false);
  const btnFilterRef = useRef<HTMLButtonElement>(null);

  const btnFilterClickCallback = useCallback(() => setIsLayerOpen(!isLayerOpen), [isLayerOpen]);

  return (
    <section className="schedule-filter">
      <div className="schedule-filter-btn">
        <button
          type="button"
          className={`btn-filter ${isLayerOpen ? 'is-active' : ''}`}
          onClick={btnFilterClickCallback}
          ref={btnFilterRef}
          data-contents="달력_필터_클릭"
        >
          <span className="hidden">{l10n?.filter?.btnFilterText}</span>
        </button>
      </div>
      <FilterLayer
        data={data}
        l10n={l10n}
        isLayerOpen={isLayerOpen}
        setIsLayerOpen={setIsLayerOpen}
        btnFilterRef={btnFilterRef}
      />
    </section>
  );
};

export default Filter;
